import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/AuthContext';
import { BusinessProvider } from './components/BusinessContext';
import { Sidebar } from './components/Sidebar';
import { MobileHeader } from './components/MobileHeader';
import { TopHeader } from './components/TopHeader';
import { Discover } from './pages/Discover';
import { Shop } from './pages/Shop';
import { Help } from './pages/Help';
import { ListingDetail } from './pages/ListingDetail';
import { Requests } from './pages/Requests';
import { Messages } from './pages/Messages';
import { Saved } from './pages/Saved';
import { MyAI } from './pages/MyAI';
import { Profile } from './pages/Profile';
import { SellFlow } from './pages/SellFlow';
import { BusinessHub } from './pages/BusinessHub';
import { BusinessHubWelcomeModal } from './components/BusinessHubWelcomeModal';
// Gates a route behind authentication. While signed out, it redirects to
// Discover and opens the login prompt so only Discover is usable.
function ProtectedRoute({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isAuthenticated,
    openLogin
  } = useAuth();
  useEffect(() => {
    if (!isAuthenticated) openLogin();
  }, [isAuthenticated, openLogin]);
  if (!isAuthenticated) return <Navigate to="/" replace data-id="element-0" />;
  return <>{children}</>;
}
function AppShell() {
  const location = useLocation();
  // The storefront is a public page — render it without any app chrome
  // (no sidebar, top header, region selector, profile, or mobile nav).
  const isPublicShop = location.pathname.startsWith('/shop/');
  if (isPublicShop) {
    return <main className="app-min-fill flex flex-col min-h-screen bg-white" data-id="element-1">
        <Routes data-id="element-2">
          <Route path="/shop/:slug" element={<Shop data-id="element-4" />} data-id="element-3" />
        </Routes>
      </main>;
  }
  return <div className="app-min-fill flex min-h-screen bg-white" data-id="element-5">
      <Sidebar data-id="element-6" />

      <div className="flex-1 min-w-0 flex flex-col relative overflow-x-hidden" data-id="element-7">
        <MobileHeader data-id="element-8" />
        <TopHeader data-id="element-9" />

        <main className="app-min-fill flex flex-col flex-1 overflow-x-hidden [&>*]:flex-1 [&>*]:min-h-0" data-id="element-10">
          <Routes data-id="element-11">
            <Route path="/" element={<Discover data-id="element-13" />} data-id="element-12" />
            <Route path="/listing/:id" element={<ProtectedRoute data-id="element-15">
                  <ListingDetail data-id="element-16" />
                </ProtectedRoute>} data-id="element-14" />
            <Route path="/requests" element={<ProtectedRoute data-id="element-18">
                  <Requests data-id="element-19" />
                </ProtectedRoute>} data-id="element-17" />
            <Route path="/saved" element={<ProtectedRoute data-id="element-21">
                  <Saved data-id="element-22" />
                </ProtectedRoute>} data-id="element-20" />
            <Route path="/messages" element={<ProtectedRoute data-id="element-24">
                  <Messages data-id="element-25" />
                </ProtectedRoute>} data-id="element-23" />
            <Route path="/my-ai" element={<ProtectedRoute data-id="element-27">
                  <MyAI data-id="element-28" />
                </ProtectedRoute>} data-id="element-26" />
            <Route path="/business" element={<ProtectedRoute data-id="element-30">
                  <BusinessHub data-id="element-31" />
                </ProtectedRoute>} data-id="element-29" />
            <Route path="/profile" element={<ProtectedRoute data-id="element-33">
                  <Profile data-id="element-34" />
                </ProtectedRoute>} data-id="element-32" />
            <Route path="/sell" element={<ProtectedRoute data-id="element-36">
                  <SellFlow data-id="element-37" />
                </ProtectedRoute>} data-id="element-35" />
            <Route path="/help" element={<ProtectedRoute data-id="element-39">
                  <Help data-id="element-40" />
                </ProtectedRoute>} data-id="element-38" />
          </Routes>
        </main>
      </div>
    </div>;
}
export function App() {
  // Everyone can browse + chat freely. Registration is only required for
  // gated actions (e.g. viewing full listing details) via AuthProvider.
  // Business Hub activates adaptively via BusinessProvider.
  return <AuthProvider data-id="element-41">
      <BusinessProvider data-id="element-42">
        <BrowserRouter data-id="element-43">
          <AppShell data-id="element-44" />
          <BusinessHubWelcomeModal data-id="element-45" />
        </BrowserRouter>
      </BusinessProvider>
    </AuthProvider>;
}